/**
 * CornerstonesSection — section 4 of the homepage (Konzept § 5 Z. 129).
 *
 * Job: branch. Each card owns exactly one path into the offer — the visitor
 * self-sorts by recognising his own situation, not by picking a category.
 *
 * Since 7.8.2026 the homepage runs four cards (Lage statt Sortiment); the
 * column count follows the card count, see the grid below. Other pages using
 * this component keep three and are unaffected.
 *
 * The whole card is the link — there is no per-card CTA button on purpose: the
 * primary CTA appears exactly twice on the page (header + closing block), and a
 * CTA chasing the visitor down every section reads as needy (Konzept Z. 135).
 *
 * Spotlight (F4, 2.8.2026). Gemessen an der gebauten Startseite trug diese
 * Sektion `bg-white py-24` — dieselbe Kombination wie fuenf andere. Von 13
 * Sektionen hoben sich nur Hero und Kontakt ab; die Eckpfeiler lagen im
 * Mittelfeld, und die in F5 ergaenzten Karussells uebertrafen sie um das
 * Achtfache an Flaeche (5326 zu 613 Zeichen).
 *
 * «Spotlight» heisst hier NICHT eine Karte hervorheben — die drei Eckpfeiler
 * sind gleichrangig (Fundament § 9.1). Gehoben wird die Sektion als Ganzes:
 * eigener dunkler Ton (`bg-primary-dark`, wie `PerksSection` und
 * `PlatformOverviewSection`), mehr Hoehe, groessere Karten. Bewusst kein
 * Farbverlauf — der bleibt Hero und Kontakt vorbehalten, sonst konkurrieren
 * drei Bloecke um denselben Rang.
 */

import React from "react";
import { Link } from "react-router-dom";
import { type SectionCornerstonesProps } from "../../content/section-cornerstones";
import { useLocalizedUrl } from "../../hooks";
import { isJourney, trackJourneyStep } from "../../utils/tag-manager";
import { SectionBadge } from "../ui";

/*
 * Haendisch gepflegt und damit ein Duplikat von `cornerstoneCardSchema`
 * (`content/section-cornerstones.ts`). Beide Listen muessen uebereinstimmen —
 * das `journey`-Feld vom 7.8.2026 fiel hier zuerst durch und brach den
 * Typecheck, obwohl das Schema laengst passte.
 */
type CornerstoneCardLinkProps = {
    title: string;
    value?: string;
    description: string;
    href: string;
    ariaLabel?: string;
    icon?: string;
    linkText?: string;
    journey?: string;
    badge?: string;
    ready?: string;
    built?: string;
    /**
     * Fuenf Rasterzeilen statt drei — gesetzt, sobald EINE Karte der Sektion
     * eine `value`-Zeile fuehrt (Jan, 8.10.2026). Die Sektion entscheidet,
     * nicht die Karte: Alle Karten muessen dieselbe Zeilenzahl erben.
     */
    withValueRow?: boolean;
};

/**
 * Bricht den Kartentitel nach dem Doppelpunkt um (F4, 2.8.2026).
 *
 * Alle Eckpfeiler-Titel folgen «Schlagwort: Erlaeuterung». Ohne feste Regel
 * bricht der Satz dort, wo die Spalte zufaellig endet — im gebauten Artefakt
 * stand «Engineering: our services» einzeilig neben zweizeiligen Nachbarn, und
 * DE brach mitten in der Erlaeuterung. Bei drei gleichrangigen Karten liest
 * sich das als Rangfolge.
 *
 * Der Doppelpunkt bleibt am Schlagwort, der Rest beginnt die zweite Zeile.
 * Titel OHNE Doppelpunkt (die `/angebote`-Katalogkarten nutzen dieselbe
 * Komponente) bleiben unveraendert — deshalb der Rueckfall statt eines
 * erzwungenen Umbruchs.
 */
const splitTitleAtColon = (title: string): [string, string] | null => {
    const at = title.indexOf(":");
    if (at < 0 || at === title.length - 1) {
        return null;
    }
    return [title.slice(0, at + 1), title.slice(at + 1).trim()];
};

/**
 * A single cornerstone card. Split out so each card can localise its own href —
 * hooks cannot be called inside a map callback in the parent.
 *
 * The `icon` is optional: cards without one (the homepage cornerstones) render exactly
 * as before; cards with one (the /angebote catalog, Etappe 5c) show an emoji above the
 * title. It is decorative, so it is hidden from assistive tech (aria-hidden) — the
 * card's meaning lives in the title and description.
 */
/**
 * Der Nachsatz unter den Kacheln — fuer die, die sich in keiner wiederfinden.
 *
 * **Er ersetzt eine vierte Kachel** (Jan, 16.9.2026). Die Workshops sind nicht
 * der vierte Weg, sondern die Sortierung auf einen der drei; als gleichwertige
 * Karte gelesen, boten sie eine Wahl an, die es nicht gibt.
 *
 * Der Text traegt einen Inline-Link (`<a href>` im Content, wie auf vier
 * anderen Seiten). Der Klick darauf oeffnet denselben Trichter wie eine Kachel,
 * aber mit eigenem `journey_entry`: «hinweis» statt «wegweiser». Ohne die
 * Unterscheidung saehe die Auswertung zwei verschiedene Einstiege als einen —
 * «ich erkenne meine Lage» und «ich erkenne sie nicht».
 */
const CornerstoneHinweis: React.FC<{ text: string; journey?: string }> = ({ text, journey }) => {
    const handleClick = (event: React.MouseEvent<HTMLParagraphElement>): void => {
        // Nur der Link zaehlt, nicht der Absatz drumherum.
        if ((event.target as HTMLElement).closest("a") && isJourney(journey)) {
            trackJourneyStep({ journey, step: 1, entry: "hinweis" });
        }
    };

    return (
        <p
            className="mx-auto mt-10 max-w-3xl text-center text-lg text-white/70 [&_a]:font-semibold [&_a]:text-white [&_a]:underline [&_a]:underline-offset-4 [&_a:hover]:text-brand-mint"
            onClick={handleClick}
            dangerouslySetInnerHTML={{ __html: text }}
        />
    );
};

const CornerstoneCardLink: React.FC<CornerstoneCardLinkProps> = ({
    title,
    value,
    description,
    href,
    ariaLabel,
    icon,
    linkText,
    journey,
    badge,
    ready,
    built,
    withValueRow = false,
}) => {
    const localizedHref = useLocalizedUrl(href);
    const titleParts = splitTitleAtColon(title);

    /*
     * Station 1 der Reise, nur wenn die Karte eine eroeffnet (7.8.2026).
     *
     * Ohne `journey` faellt der Klick still durch — so bleibt `/angebote/ki-anwendungen`,
     * das dieselbe Komponente nutzt, ohne Events. Das Feld steht deshalb im
     * Content und nicht hier: Die Komponente muss nicht wissen, auf welcher
     * Seite sie gerade steht.
     *
     * Der Wert wird gegen `JOURNEYS` geprueft, weil Velite aus MDX nur `string`
     * ableiten kann. Ein Tippfehler im Content wuerde sonst als eigene Reise im
     * Reporting auftauchen und dort erst Monate spaeter auffallen.
     */
    const handleClick = (): void => {
        if (isJourney(journey)) {
            trackJourneyStep({ journey, step: 1, entry: "wegweiser" });
        }
    };

    const heading = titleParts ? (
        <>
            {titleParts[0]}
            {/* Der Umbruch ist rein visuell. Das Leerzeichen bleibt im Text,
            weil `<br>` zu `textContent` nichts beitraegt: ohne es lasen
            Screenreader «Engineering:Unsere Dienstleistungen». Es steht
            vor dem Umbruch, damit es visuell am Zeilenende verschwindet. */}
            <span className="sr-only"> </span>
            <br />
            {titleParts[1]}
        </>
    ) : (
        title
    );

    const details = (
        <>
            <p className="text-base leading-relaxed text-white/80">{description}</p>
            {/*
                Was bereitsteht und was neu gebaut wird (2.9.2026).

                Beide Felder oder keiner — eine Karte mit `ready` allein zeigt
                die halbe Rechnung und liest sich, als bekaeme der Kunde alles
                fertig. `startseite-cds-nutzen.spec.ts` haelt das fest.

                Zwei Zeilen mit gemeinsamer Trennlinie, KEIN Raster: In der
                schmalen dritten Spalte stuenden zwei Kaesten nebeneinander mit
                je vier Woertern pro Zeile. Untereinander liest sich der
                Gegensatz auch dort.

                Die Beschriftungen sind Text, kein `<dt>`: Ein Screenreader
                liest «Steht schon: Anmeldung…» als einen Satz. Als
                Definitionsliste kaeme zwischen Begriff und Wert eine Pause,
                die den Gegensatz zerlegt.
            */}
            {ready && built && (
                <div className="mt-5 border-t border-white/15 pt-4 text-sm leading-relaxed">
                    <p className="text-white/70">
                        <span className="font-semibold text-secondary">Steht schon: </span>
                        {ready}
                    </p>
                    <p className="mt-2 text-white/70">
                        <span className="font-semibold text-white">Neu gebaut: </span>
                        {built}
                    </p>
                </div>
            )}
        </>
    );

    return (
        <Link
            to={localizedHref}
            onClick={handleClick}
            aria-label={ariaLabel}
            className={[
                // `grid` + `row-span-*` + `grid-rows-subgrid`: Die Karte ist
                // selbst Rasterzelle und uebernimmt die Zeilen des
                // Aussenrasters. Dadurch beginnt jedes Band auf derselben Hoehe
                // wie in den Nachbarkarten — Flexbox kann das nicht, weil jede
                // Karte fuer sich rechnet (Entscheid Jan, 16.9.2026: «Badge,
                // Text und Link» ausrichten).
                //
                // Ohne Wertzeile DREI Baender (`row-span-3`): Badge,
                // Titel+Text, Link. Titel und Beschreibung teilen sich eines
                // (Entscheid Jan, 16.9.2026) — als getrennte Zeilen fiel der
                // Abstand zwischen Titel und Text weg, weil die Zeilenhoehe ihn
                // schluckte («Wir bauen die Anwendung» klebte unter
                // «Kundenanfragen.»).
                //
                // Mit Wertzeile FUENF Baender (`row-span-5`, Jan, 8.10.2026):
                // Badge, Titel, Wert, Text, Link. Die Titel der Startseite
                // laufen bei 1280px ueber 5, 5 und 3 Zeilen; im gemeinsamen
                // Band begannen Wert und Text deshalb auf drei Hoehen. Als
                // eigene Zeilen beginnen sie in allen Karten auf einer Linie,
                // und der Platz, den ein kurzer Titel uebrig laesst, steht
                // UNTER dem Titel. Den Abstand traegt hier `gap-y-4` (16px,
                // derselbe Rhythmus wie das fruehere `mb-4`), nicht eine
                // Marge — so wird er nicht von der Zeilenhoehe geschluckt.
                // Badge und Link behalten ihre 24px ueber `pb-2` / `pt-2`.
                //
                // `h-full` faellt weg: Als Rasterzelle fuellt die Karte ihre
                // Zeilen ohnehin. `flex-col` ebenso — das Raster ordnet jetzt.
                // Der Ein-Spalten-Fall unter `md:` bleibt unberuehrt: Dort hat
                // das Aussenraster eine Spalte, und `subgrid` erbt die Zeilen
                // dieser einen Karte.
                withValueRow
                    ? "group grid row-span-5 grid-rows-subgrid gap-y-4 rounded-2xl border border-white/15 bg-white/5 p-10"
                    : "group grid row-span-3 grid-rows-subgrid gap-y-6 rounded-2xl border border-white/15 bg-white/5 p-10",
                "transition hover:border-white/40 hover:bg-white/10 hover:shadow-xl",
                "focus:outline-none focus-visible:ring-2 focus-visible:ring-white",
            ].join(" ")}
        >
            {/*
                Icon UND Badge in derselben Rasterzeile (16.9.2026). Die Karte
                erbt drei Zeilen (`row-span-3`) oder, mit Wertzeile, fuenf
                (`row-span-5`, 8.10.2026); ein Kind mehr schoebe alle Baender
                um eins und der Link fiele aus der Karte. Der Wrapper steht
                deshalb immer da, auch wenn beide Felder fehlen — eine leere
                erste Zeile kostet nichts, eine fehlende zerstoert die
                Ausrichtung.
            */}
            <div className={withValueRow ? "pb-2" : undefined}>
                {icon && (
                    <span className="mb-2 block text-4xl" aria-hidden="true">
                        {icon}
                    </span>
                )}
                {/*
                Der Leistungsname als Augenbraue — links, farbig, ohne Pille
                (Jan, 12.8.2026).

                **Warum keine Pille mehr.** Der erste Wurf nutzte `SectionBadge`
                mit Variante `on-dark`, oben rechts. Am Artefakt nachgemessen war
                der TEXT nicht das Problem (weiss auf der Pille: 8.3:1) — die
                PILLE war es: ihre Flaeche (`white/15`) steht gegen die Karte
                (`white/5`) bei rund 1.35:1. Als Form praktisch unsichtbar,
                hinterliess sie nur einen Schleier um die Schrift.

                Entweder die Kapsel richtig bauen oder weglassen. Weglassen ist
                hier richtig, denn die ganze Karte IST der Link — eine Pille
                sieht aus wie ein Knopf darauf. Aus demselben Grund trug sie
                schon vorher kein Sparkle-Symbol.

                **Warum `text-secondary`.** Mint (#8ce6d1) auf der Karte ergibt
                9.0:1 — AAA auch fuer Kleinschrift — und macht den Namen zum
                einzigen nicht-weissen Element der Karte. Genau die Rolle einer
                Augenbraue. Die Farbe ist der Hausakzent, siehe
                `WhitepaperSection` und `TestimonialSection`.

                **Warum links.** Titel, Beschreibung und Linkzeile stehen links;
                rechts schwebte der Name weg von dem, was er beschriftet.

                **Warum `text-base`.** Dasselbe Mass wie die Linkzeile am
                Kartenfuss (Jan, 12.8.2026): Der Name oben und der Ausgang unten
                rahmen die Karte in gleicher Groesse, mint-fett gegen
                weiss-halbfett. `text-xs` war zu klein, um als eigene Ebene
                gelesen zu werden.

                Kein `uppercase`: «ERPROBEN · PRODUCT-MARKET-TEST» waere lang und
                laut. Die Farbe traegt die Unterscheidung bereits.

                Im Fluss, nicht `absolute` — ein absolut gesetzter Name kann bei
                schmalen Spalten die erste Titelzeile ueberlagern.
            */}
                {badge && <span className="block text-base font-bold text-secondary">{badge}</span>}
            </div>
            {/*
                Ohne Wertzeile: Titel und Beschreibung in EINEM Band (Entscheid
                Jan, 16.9.2026: «Titel und Text sollten wir nicht trennen»). Als
                getrennte Rasterzeilen verlor der Titel seinen Abstand zum Text —
                die Zeilenhoehe schluckte ihn. Der Abstand steht dort als `mb-4`
                am Titel.

                Mit Wertzeile (Jan, 8.10.2026): Titel, Wert und Text als drei
                eigene Zeilen, damit Wert und Text in allen Karten auf derselben
                Hoehe beginnen. Den Abstand traegt dann `gap-y-4` der Karte.
                Eine Karte ohne `value` setzt ein leeres Element in die
                Wertzeile — fehlte es, rutschten Text und Link eine Zeile hoch.
            */}
            {withValueRow ? (
                <>
                    <h3 className="text-2xl font-black text-white">{heading}</h3>
                    {value ? <p className="text-lg font-bold text-secondary">{value}</p> : <div />}
                    <div>{details}</div>
                </>
            ) : (
                <div>
                    <h3 className="mb-4 text-2xl font-black text-white">{heading}</h3>
                    {details}
                </div>
            )}
            {/*
                Sichtbares Linkmerkmal (Jan, 3.8.2026). Die Karte war schon
                klickbar, sah im Ruhezustand aber nicht danach aus — sichtbar
                wurde es erst beim Hover.

                Ein `span`, kein `a`: die ganze Karte IST bereits der Link, und
                ein Link im Link ist ungueltiges HTML. Der Pfeil ist Dekoration,
                die Zeile traegt keinen eigenen Namen — sonst laese ein
                Screenreader den Kartennamen und danach «Mehr erfahren» als
                zweites, zusammenhangloses Ziel.
            */}
            {/*
                `block` statt `inline-flex` (Jan, 5.8.2026): als Flex-Container
                waren Text und Pfeil zwei Kaesten. Flex bricht Kaesten um, nicht
                Woerter — brach das Label in der schmalen Karte, rutschte der
                Pfeil neben den GANZEN Block statt hinter das letzte Wort.

                Der Pfeil haengt jetzt per GESCHUETZTEM Leerzeichen am letzten
                Wort — mit normalem Leerzeichen stuende er bei knappem Platz
                allein auf der letzten Zeile.
            */}
            {linkText && (
                <span
                    aria-hidden="true"
                    // Kein `mt-auto pt-6` mehr: Die `1fr`-Zeile darueber
                    // schluckt den Laengenunterschied, der Link sitzt dadurch
                    // von selbst unten — und zwar auf derselben Linie wie in
                    // den Nachbarkarten, was `mt-auto` je Karte nicht konnte.
                    // Fehlt `linkText`, bleibt die letzte Zeile leer und die
                    // Nachbarn behalten ihre Ausrichtung. `pt-2` nur mit
                    // Wertzeile: ergaenzt `gap-y-4` auf die 24px von `gap-y-6`.
                    className={
                        withValueRow
                            ? "block self-end pt-2 text-base font-semibold text-white underline-offset-4 group-hover:underline"
                            : `block self-end text-base font-semibold text-white
                        underline-offset-4 group-hover:underline`
                    }
                >
                    {linkText}
                    {" →"}
                </span>
            )}
        </Link>
    );
};

export type CornerstonesSectionProps = SectionCornerstonesProps & {
    /**
     * Sprungziel der Anker-Nav (12.8.2026) — diese Sektion ist Kapitel 3 der
     * Startseite, aber kein `ChapterSection`: Sie bringt ihren eigenen Kopf und
     * ihren eigenen dunklen Ton mit, ein Gefaess darum haette eine zweite
     * Ueberschrift ergeben.
     *
     * Optional, weil `/angebote` und `/angebote/ki-anwendungen` dieselbe Komponente ohne
     * Sprungziel nutzen.
     */
    anchorId?: string;
    /** Das Kapitel-Etikett, aus derselben Quelle wie die Nav-Beschriftung. */
    badge?: string;
};

export const CornerstonesSection: React.FC<CornerstonesSectionProps> = ({
    title,
    subtitle,
    cards,
    anchorId,
    badge,
    hinweis,
    hinweisJourney,
}) => {
    if (!cards?.length) {
        return null;
    }

    // Eine Karte mit `value` stellt die ganze Sektion auf fuenf Zeilen um
    // (Jan, 8.10.2026). Ohne `value` bleibt alles bei drei Zeilen — so
    // rendern `/angebote/ki-anwendungen` und jede andere Seite wie bisher.
    const withValueRow = cards.some(card => Boolean(card.value));

    return (
        <section
            id={anchorId}
            data-testid="cornerstones-section"
            className="scroll-mt-24 bg-primary-dark pt-28 pb-20 text-white"
        >
            <div className="mx-auto w-full px-4 md:px-12">
                {(badge || title || subtitle) && (
                    /*
                     * Zentrierter Sektionskopf (Jan, 6.8.2026) — dieselbe Korrektur
                     * wie in `SpektrumSection`. `max-w-3xl` ohne `mx-auto` liess den
                     * Block links stehen, waehrend die Sektionen darueber und darunter
                     * (Assistant, Trust, Problem, How-we-work) zentriert sind.
                     *
                     * Nur der Kopf: die drei Eckpfeiler-Karten darunter bleiben
                     * linksbuendig, sie sind Lesetext.
                     */
                    <div className="mb-14 max-w-3xl mx-auto text-center">
                        {/* `SectionBadge` wie auf allen anderen Seiten. Der
                            1.35:1-Befund weiter unten (Zeile ~136) gilt NICHT hier: er
                            betrifft die Pille auf einer `white/5`-KARTE. Dieser Kopf
                            steht auf dem Sektionsgrund, wo `on-dark` traegt. */}
                        {badge && <SectionBadge variant="on-dark">{badge}</SectionBadge>}
                        {title && <h2 className="mb-4 text-4xl font-black text-white md:text-5xl">{title}</h2>}
                        {subtitle && <p className="text-lg text-white/80">{subtitle}</p>}
                    </div>
                )}
                {/*
                    Die Spaltenzahl folgt der Kartenzahl, gedeckelt bei drei —
                    dieselbe Regel wie in `SpektrumSection` (Jan, 5.8.2026).
                    Vier Karten: auf Tablet 2x2, ab Desktop alle nebeneinander.
                    Im Dreier-Raster haenge die vierte allein in der zweiten
                    Reihe, und genau die traegt auf der Startseite die Lage
                    «Sie koennen sich nicht entscheiden» — die haeufigste, nicht
                    die nachrangige. Optisch als Nachtrag waere das falsch.
                */}
                <div
                    // `grid-auto-rows`, nicht `grid-template-rows`: Die Liste
                    // wiederholt sich zyklisch ueber alle impliziten Zeilen.
                    // Vier Karten unter `md:grid-cols-2` ergeben zwei
                    // Kartenreihen — die zweite Reihe bekommt dieselben
                    // Baender wie die erste. `grid-template-rows` mit festen
                    // Werten liesse die Zeilen der zweiten Reihe auf `auto`
                    // fallen, und die untere Kartenreihe haette nichts zu erben.
                    //
                    // Ohne Wertzeile DREI Baender: Badge, Titel+Text, Link.
                    // Mit Wertzeile FUENF (Jan, 8.10.2026): Badge, Titel,
                    // Wert, Text, Link. Das Band mit dem Text ist jeweils
                    // `1fr` und schluckt den Laengenunterschied der
                    // Beschreibungen; dadurch sitzt der Link unten auf einer
                    // Linie, ohne dass die Karte ihn selbst schieben muss. Die
                    // Titelzeile ist `auto` und so hoch wie der laengste
                    // Titel — der Rest steht unter den kuerzeren Titeln.
                    className={`grid gap-8 ${
                        withValueRow ? "[grid-auto-rows:auto_auto_auto_1fr_auto]" : "[grid-auto-rows:auto_1fr_auto]"
                    } ${
                        cards.length === 4
                            ? "md:grid-cols-2 xl:grid-cols-4"
                            : cards.length === 2
                              ? "md:grid-cols-2"
                              : "md:grid-cols-3"
                    }`}
                >
                    {cards.map(card => (
                        <CornerstoneCardLink key={card.href} {...card} withValueRow={withValueRow} />
                    ))}
                </div>
                {hinweis && <CornerstoneHinweis text={hinweis} journey={hinweisJourney} />}
            </div>
        </section>
    );
};

export default CornerstonesSection;
